import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, refreshUser } = useAuth();
  
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const checkPaymentStatus = useCallback(async () => {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${API_URL}/api/subscription/status/${sessionId}`, true);
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      xhr.onload = function() {
        try {
          if (xhr.status >= 200 && xhr.status < 300) {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } else {
            console.error('Status check failed:', xhr.responseText);
            resolve(null);
          }
        } catch (e) {
          console.error('Error parsing status:', e);
          resolve(null);
        }
      };
      
      xhr.onerror = function() {
        console.error('Network error checking status');
        resolve(null);
      };
      
      xhr.send();
    });
  }, [sessionId, token, API_URL]);

  const pollPaymentStatus = useCallback(async (attemptCount = 0) => {
    const maxAttempts = 30; // 60 seconds total (30 * 2 seconds)
    setPollCount(attemptCount);
    
    if (attemptCount >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    const data = await checkPaymentStatus();
    
    if (data) {
      setPaymentData(data);

      // Check multiple success conditions
      const isPaid = data.payment_status === 'paid' || 
                     data.status === 'complete' || 
                     data.subscription_status === 'active';

      if (isPaid) {
        setStatus('success');
        // Meta Pixel — Purchase. Value is sourced from our OWN canonical plan
        // pricing keyed by plan_id (returned at the TOP LEVEL of the
        // /api/subscription/status/{session_id} JSON — the backend strips
        // Stripe's session.metadata object and re-exposes plan_id directly).
        // We do NOT use Stripe's amount_total — that field can transiently be
        // 0 during Stripe's async invoice finalization on the very first
        // success poll, producing $0 Purchase events. Free trials never reach
        // this page (separate manual URL flow), so we always have a real paid
        // plan_id to look up. sessionStorage guards against duplicate fires on
        // refresh / re-poll.
        try {
          const firedKey = `vf_fbq_purchase_${sessionId}`;
          if (typeof window.fbq === 'function' && !sessionStorage.getItem(firedKey)) {
            const PLAN_PRICES_USD = {
              personal: 9,
              influencer_starter: 19,
              influencer_pro: 79.99,
              brand_growth: 99,
              brand_starter: 199,
              brand_enterprise: 399,
            };
            const planId = data.plan_id;
            const value = PLAN_PRICES_USD[planId];
            if (typeof value === 'number') {
              window.fbq('track', 'Purchase', { value: value, currency: 'USD' });
              sessionStorage.setItem(firedKey, '1');
            } else {
              console.warn('Meta Pixel Purchase skipped: unknown plan_id', planId);
            }
          }
        } catch (e) {
          console.warn('Meta Pixel Purchase event failed:', e);
        }
        await refreshUser();
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }
    }

    // Keep polling
    setTimeout(() => pollPaymentStatus(attemptCount + 1), 2000);
  }, [checkPaymentStatus, refreshUser]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(0);
    } else {
      setStatus('error');
    }
  }, [sessionId, pollPaymentStatus]);

  const getDashboardMessage = () => {
    if (!user) return "Go to Dashboard";
    switch(user.user_type) {
      case 'brand':
        return "Go to Brand Dashboard";
      case 'influencer':
        return "Go to Influencer Dashboard";
      default:
        return "Start Trying On Clothes";
    }
  };

  const getWelcomeMessage = () => {
    if (!user) return "Your subscription is now active!";
    switch(user.user_type) {
      case 'brand':
        return "Welcome to VirtualFit for Brands! Start uploading your clothing portfolio and reduce returns.";
      case 'influencer':
        return "Welcome to VirtualFit! Set up your profile, add your affiliate links, and start creating stunning virtual try-on content.";
      default:
        return "Welcome to VirtualFit! You can now try on clothes virtually before you buy.";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-orange-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        
        {/* Checking Payment */}
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-rose-500 mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Processing Payment</h1>
            <p className="text-gray-600">Please wait while we confirm your payment...</p>
            <p className="text-sm text-gray-400 mt-4">Checking... ({pollCount}/30)</p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
              <div 
                className="bg-gradient-to-r from-rose-500 to-orange-500 h-2 rounded-full transition-all" 
                style={{width: `${(pollCount / 30) * 100}%`}}
              />
            </div>
          </>
        )}

        {/* Success */}
        {status === 'success' && (
          <>
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Thank You! 🎉</h1>
            <p className="text-gray-600 mb-6">{getWelcomeMessage()}</p>
            
            {paymentData && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm text-gray-500">Payment confirmed</p>
                <p className="font-semibold text-gray-800">${paymentData.amount} {paymentData.currency?.toUpperCase()}</p>
              </div>
            )}
            
            <Link 
              to="/dashboard"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              {getDashboardMessage()} <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            
            {user?.handle && (
              <p className="mt-4 text-sm text-gray-500">
                Your profile: <Link to={`/@${user.handle}`} className="text-rose-500 hover:underline">@{user.handle}</Link>
              </p>
            )}
          </>
        )}

        {/* Timeout - Payment might still be processing */}
        {status === 'timeout' && (
          <>
            <Loader2 className="w-16 h-16 text-amber-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Processing</h1>
            <p className="text-gray-600 mb-4">
              Your payment is taking longer than expected. This can happen if:
            </p>
            <ul className="text-left text-gray-600 mb-6 space-y-2 max-w-md mx-auto text-sm">
              <li>• The Stripe checkout window was closed before completing payment</li>
              <li>• Your bank requires additional verification (3D Secure)</li>
              <li>• There's a temporary delay with payment processing</li>
            </ul>
            <div className="space-y-3">
              <Link 
                to="/dashboard"
                className="block w-full px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold"
              >
                Check Dashboard
              </Link>
              <button 
                onClick={() => { setStatus('checking'); pollPaymentStatus(0); }}
                className="block w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200"
              >
                Check Again
              </button>
              <Link 
                to="/subscribe"
                className="block w-full px-6 py-3 text-rose-500 underline text-sm"
              >
                Try subscribing again
              </Link>
            </div>
          </>
        )}

        {/* Error */}
        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Issue</h1>
            <p className="text-gray-600 mb-6">
              There was an issue with your payment. Please try again or contact support.
            </p>
            <div className="space-y-3">
              <Link 
                to="/subscribe"
                className="block w-full px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold"
              >
                Try Again
              </Link>
              <Link 
                to="/contact"
                className="block w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200"
              >
                Contact Support
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
